import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ReclassificationPeriod,
    ReclassificationStatement,
} from '@modeso/types__aumico-lib-annualreport-be';
import { I18NextService } from 'angular-i18next';
import { displayAccountNumber } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassAccountNumber';
import { keyOf } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/reclassificationAccountKey';

/**
 * REV-21798: a reclassification target is either a concrete account or a
 * *container* — an FLI, a SubFLI, or a group defined in Grouping & Masking.
 *
 * Containers are a pure UI affordance. The transaction model stays strictly
 * account → account: when the user picks a container, the parent component
 * materializes (or reuses) a `MAN` account inside it and rewrites the target
 * to that account before anything reaches the store. Nothing downstream —
 * engine, validator, notes, backend — learns about containers.
 *
 * Mapping template groups are deliberately NOT offered: accounts cannot be
 * mapped directly into them, only into the FLIs/SubFLIs they contain.
 */
/**
 * `NEW_GROUP` is a DRAFT: a group the user described in the "+" dialog that does not exist yet. It
 * becomes a real group (with its `MAN` account) when the modal is SAVED, so cancelling leaves the
 * grouping untouched.
 */
export type ReclassTargetKind = 'ACCOUNT' | 'FLI' | 'SUBFLI' | 'GM_GROUP' | 'NEW_GROUP';

export type ReclassTargetSelection =
    | { kind: 'ACCOUNT'; ref: IReclassificationAccountRef }
    | {
          kind: Exclude<ReclassTargetKind, 'ACCOUNT'>;
          /** Stable id of the container — its `sortingId`, or the groupId for a G&M group. */
          containerId: string;
          label: string;
          /**
           * Where the generated `MAN` account has to be placed. Resolved when
           * the option is built (the parent is walking the tree anyway), so
           * materialization does not need a second traversal. Opaque to this
           * component.
           */
          hostFliId: string;
          hostSubFliId?: string;
          /** Set for `GM_GROUP` only — the account also joins this group. */
          groupId?: string;
          /** Set for `NEW_GROUP` only — the caption to create the group under, on save. */
          groupName?: string;
      };

/** Target candidate displayed in the dropdown. */
export interface ReclassTargetOption {
    /** `<option>` value — unique across accounts and containers alike. */
    value: string;
    label: string;
    /** Same-statement membership ('BS' or 'IS') — used for filtering. */
    statement: ReclassificationStatement;
    selection: ReclassTargetSelection;
}

/** Local editable entry — one row in the modal. */
interface EditableEntry {
    /** Existing transaction id, or undefined for a new draft. */
    id?: string;
    target: ReclassTargetSelection | null;
    amount: number | null;
    description: string;
    /** UI-only key for *ngFor tracking. */
    uiKey: string;
}

/** Output payload when the user saves the modal. */
export interface ReclassModalSavePayload {
    sourceRef: IReclassificationAccountRef;
    period: ReclassificationPeriod;
    statement: ReclassificationStatement;
    entries: Array<{
        id?: string;
        target: ReclassTargetSelection;
        amount: number;
        description?: string;
    }>;
}

/** Stable `<option>` value / dedup key for any selection. */
export function targetSelectionKey(selection: ReclassTargetSelection): string {
    return selection.kind === 'ACCOUNT'
        ? `acc|${keyOf(selection.ref)}`
        : `${selection.kind}|${selection.containerId}`;
}

@Component({
    selector: 'aumico-reclassification-modal',
    templateUrl: './reclassification-modal.component.html',
    styleUrls: ['./reclassification-modal.component.scss'],
})
export class ReclassificationModalComponent implements OnInit, OnChanges {

    /**
     * REV-21798: account number as shown to the user. A system-created reclassification account
     * carries an internal `MAN-xxxxxxxx` identity that must never be surfaced — every user-facing
     * rendering shows plain `MAN`.
     */
    displayNumber(accountNumber: string | null | undefined): string {
        return displayAccountNumber(accountNumber);
    }
    /** Source account ref. */
    @Input() sourceRef!: IReclassificationAccountRef;

    /** Source account human label (number + name). */
    @Input() sourceLabel = '';

    /** Source balance for the active period (used in the "exceeds balance" warning). */
    @Input() sourceBalance: number | null = null;

    /** Period being edited. */
    @Input() period!: ReclassificationPeriod;

    /** Statement of the source. Targets are filtered to the same statement. */
    @Input() statement!: ReclassificationStatement;

    /** Existing transactions where this account is the SOURCE in this period. */
    @Input() existingOutgoing: IReclassificationTransaction[] = [];

    /** Existing transactions where this account is the TARGET in this period (read-only). */
    @Input() existingIncoming: IReclassificationTransaction[] = [];

    /** Eligible target accounts (same statement as source; source itself is filtered out by this component). */
    @Input() availableTargets: ReclassTargetOption[] = [];

    /** "Aktiv" / "Passiv" side of the source for the cross-side warning (null for IS). */
    @Input() sourceSide: 'AKTIV' | 'PASSIV' | null = null;

    /** Side resolver for any target ref (returns null for IS or unknown). */
    @Input() targetSideResolver: (ref: IReclassificationAccountRef) => 'AKTIV' | 'PASSIV' | null = () => null;

    @Output() saveClicked = new EventEmitter<ReclassModalSavePayload>();
    @Output() cancelClicked = new EventEmitter<void>();
    @Output() createAccountRequested = new EventEmitter<{
        statement: ReclassificationStatement;
        onCreated: (ref: IReclassificationAccountRef) => void;
        /**
         * Used by the GROUP branch: the group does not exist yet, so there is no account ref to hand
         * back — the entry points at a draft selection instead, which the parent materializes on save.
         */
        onSelected: (selection: ReclassTargetSelection) => void;
        entryUiKey: string;
    }>();

    entries: EditableEntry[] = [];
    submitAttempted = false;

    // Localized texts
    title = '';
    incomingTitle = '';
    outgoingTitle = '';
    amountLabel = '';
    targetLabel = '';
    descriptionLabel = '';
    descriptionOptionalHint = '';
    descriptionPlaceholder = '';
    amountPlaceholder = '';
    targetPlaceholder = '';
    addEntryLabel = '';
    deleteLabel = '';
    cancelLabel = '';
    saveLabel = '';
    newAccountLabel = '';
    errAmountRequired = '';
    errTargetRequired = '';
    warnExceedsBalance = '';
    warnCrossSide = '';
    warnDuplicateTarget = '';
    subtitleSourcePrefix = '';
    subtitlePeriodCY = '';
    subtitlePeriodPY = '';
    subtitleBalancePrefix = '';

    private uiKeyCounter = 0;

    constructor(private readonly i18n: I18NextService) { }

    ngOnInit(): void {
        this.initTexts();
        this.initEntries();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const c = changes['existingOutgoing'];
        if (c && !c.firstChange) {
            this.initEntries();
        }
    }

    private initTexts(): void {
        const t = (key: string): string => this.i18n.t(key) as string;
        this.title = t('aumico_grouping_masking_reclass_modal_title');
        this.incomingTitle = t('aumico_grouping_masking_reclass_modal_incoming_section');
        this.outgoingTitle = t('aumico_grouping_masking_reclass_modal_outgoing_section');
        this.amountLabel = t('aumico_grouping_masking_reclass_modal_amount_label');
        this.targetLabel = t('aumico_grouping_masking_reclass_modal_target_label');
        this.descriptionLabel = t('aumico_grouping_masking_reclass_modal_description_label');
        this.descriptionOptionalHint = t('aumico_grouping_masking_reclass_modal_description_optional');
        this.descriptionPlaceholder = t('aumico_grouping_masking_reclass_modal_description_placeholder');
        this.amountPlaceholder = t('aumico_grouping_masking_reclass_modal_amount_placeholder');
        this.targetPlaceholder = t('aumico_grouping_masking_reclass_modal_target_placeholder');
        this.addEntryLabel = t('aumico_grouping_masking_reclass_modal_add_entry');
        this.deleteLabel = t('aumico_grouping_masking_reclass_modal_delete_entry');
        this.cancelLabel = t('aumico_grouping_masking_modal_cancel');
        this.saveLabel = t('aumico_grouping_masking_modal_apply');
        this.newAccountLabel = t('aumico_grouping_masking_reclass_modal_new_account_button');
        this.errAmountRequired = t('aumico_grouping_masking_reclass_validation_amount_required');
        this.errTargetRequired = t('aumico_grouping_masking_reclass_validation_target_required');
        this.warnExceedsBalance = t('aumico_grouping_masking_reclass_validation_exceeds_balance');
        this.warnCrossSide = t('aumico_grouping_masking_reclass_validation_cross_side');
        this.warnDuplicateTarget = t('aumico_grouping_masking_reclass_validation_duplicate_target');
        this.subtitleSourcePrefix = t('aumico_grouping_masking_reclass_modal_subtitle_source');
        this.subtitlePeriodCY = t('aumico_grouping_masking_reclass_modal_period_cy');
        this.subtitlePeriodPY = t('aumico_grouping_masking_reclass_modal_period_py');
        this.subtitleBalancePrefix = t('aumico_grouping_masking_reclass_modal_subtitle_balance');
    }

    private initEntries(): void {
        if (this.existingOutgoing && this.existingOutgoing.length > 0) {
            // Persisted transactions always point at a concrete account — a
            // container selection was already resolved to one on the way in.
            this.entries = this.existingOutgoing.map((t) => ({
                id: t.id,
                target: { kind: 'ACCOUNT' as const, ref: { ...t.target } },
                amount: t.amount,
                description: t.description ?? '',
                uiKey: this.nextUiKey(),
            }));
        } else {
            this.entries = [this.makeBlankEntry()];
        }
        this.submitAttempted = false;
    }

    private makeBlankEntry(): EditableEntry {
        return { target: null, amount: null, description: '', uiKey: this.nextUiKey() };
    }

    private nextUiKey(): string {
        this.uiKeyCounter += 1;
        return 'e' + this.uiKeyCounter;
    }

    onAddEntry(): void {
        this.entries.push(this.makeBlankEntry());
    }

    onRemoveEntry(index: number): void {
        this.entries.splice(index, 1);
        // Do NOT add a blank entry when the list becomes empty — an empty list
        // is a valid state meaning "delete all reclassifications for this source".
    }

    onAmountChange(index: number, value: string | number | null): void {
        // hamInput's number ControlValueAccessor emits a number (parsed via
        // parseFloat) or null. We tolerate string here too in case the input
        // is ever swapped to a plain text field.
        if (value == null || value === '') {
            this.entries[index].amount = null;
            return;
        }
        const n = typeof value === 'number' ? value : parseFloat(value);
        this.entries[index].amount = Number.isFinite(n) ? n : null;
    }

    onTargetSelect(index: number, value: string): void {
        if (!value) {
            this.entries[index].target = null;
            return;
        }
        const option = this.availableTargets.find((o) => o.value === value);
        this.entries[index].target = option ? option.selection : null;
    }

    onDescriptionChange(index: number, value: string): void {
        this.entries[index].description = value;
    }

    onCreateNewAccount(index: number): void {
        const entry = this.entries[index];
        this.createAccountRequested.emit({
            statement: this.statement,
            entryUiKey: entry.uiKey,
            onCreated: (ref: IReclassificationAccountRef) => {
                const target = this.entries.find((e) => e.uiKey === entry.uiKey);
                if (target) {
                    target.target = { kind: 'ACCOUNT', ref: { ...ref } };
                }
            },
            onSelected: (selection: ReclassTargetSelection) => {
                const target = this.entries.find((e) => e.uiKey === entry.uiKey);
                if (target) {
                    target.target = selection;
                }
            },
        });
    }

    /** Selection key of the entry's target, used as the <option> value. */
    targetValueOf(entry: EditableEntry): string {
        return entry.target ? targetSelectionKey(entry.target) : '';
    }

    /**
     * Full label of the entry's current target, for the select's tooltip.
     *
     * Generated reclassification accounts carry their source AND their target group
     * ("MAN Umgliederung aus Debitoren Inland nach Sammelgruppe"), which is easily wider than the
     * field, and a native <select> just clips what does not fit. The `title` makes the whole label
     * readable on hover; the placeholder is used while nothing is selected, so the tooltip never
     * appears empty.
     */
    targetLabelOf(entry: EditableEntry): string {
        const value = this.targetValueOf(entry);
        if (!value) return this.targetPlaceholder;
        return this.availableTargets.find((option) => option.value === value)?.label ?? this.targetPlaceholder;
    }

    /**
     * Check if an entry has an amount validation error.
     *
     * REV-21999: a reclassification amount may be NEGATIVE. Umgliederungen routinely need
     * contra-account postings, credit-balance corrections and reversing entries, and a signed
     * amount is how the accountant expresses "move value back". No new concept is required
     * downstream: the engine has always been sign-agnostic (the source leg gets `-amount`, the
     * target leg `+amount`), so a negative amount simply reverses the direction of both legs and
     * keeps the pair summing to zero.
     *
     * Only a MISSING, non-finite or ZERO amount is invalid — zero would move nothing.
     */
    isAmountInvalid(entry: EditableEntry): boolean {
        if (entry.amount == null) return true;
        return !(Number.isFinite(entry.amount) && entry.amount !== 0);
    }

    /** Check if an entry has a target validation error. */
    isTargetInvalid(entry: EditableEntry): boolean {
        return entry.target == null;
    }

    /**
     * NET amount leaving the source — the SIGNED sum of every entry.
     *
     * REV-21999: this used to sum the positive amounts only. Now that negative entries exist, a
     * negative leg moves value back INTO the source and so has to REDUCE the total, exactly as it
     * reduces the source's subtotal in the statement. Summing only the positives would warn about
     * draining a balance the reclassification never actually touches.
     */
    get totalOutgoing(): number {
        let s = 0;
        for (const e of this.entries) {
            if (Number.isFinite(e.amount as number)) s += e.amount as number;
        }
        return s;
    }

    /**
     * True when the reclassification drains the source PAST ZERO — the state the warning describes
     * ("the source account will show a negative balance").
     *
     * Two conditions, because with signed amounts magnitude alone is not enough:
     *   1. the net moves in the SAME direction as the balance — only then can it deplete it. A net
     *      of -200'000 out of a +100'000 account does not overdraw it, it grows it to +300'000, so
     *      warning there would be a false positive.
     *   2. and it is LARGER in magnitude, which is what carries the result across zero.
     *
     * Comparing the two magnitudes without (1) would fire on every large opposite-signed entry;
     * comparing without (2) would fire on every entry at all. The sign of the balance itself is not
     * assumed: a passive/liability source carries a negative balance and depletes towards zero from
     * below, which condition (1) expresses without special-casing it.
     */
    get exceedsBalance(): boolean {
        if (this.sourceBalance == null || !Number.isFinite(this.sourceBalance)) return false;
const net = this.totalOutgoing;
if (net === 0) return false;
const sameDirection = this.sourceBalance === 0 ? net > 0 : (net > 0) === (this.sourceBalance > 0);
return sameDirection && Math.abs(net) > Math.abs(this.sourceBalance);
    }

    /** Map of selection-key -> count among entries that have a target, for duplicate detection. */
    private targetCounts(): Map<string, number> {
        const m = new Map<string, number>();
        for (const e of this.entries) {
            if (e.target) {
                const k = targetSelectionKey(e.target);
                m.set(k, (m.get(k) ?? 0) + 1);
            }
        }
        return m;
    }

    isDuplicateTarget(entry: EditableEntry): boolean {
        if (!entry.target) return false;
        const counts = this.targetCounts();
        return (counts.get(targetSelectionKey(entry.target)) ?? 0) > 1;
    }

    isCrossSide(entry: EditableEntry): boolean {
        if (!entry.target || this.sourceSide == null) return false;
        // Only meaningful for concrete accounts. A container's AKTIV/PASSIV
        // side is not resolved here — the account that will be created inside
        // it does not exist yet.
        if (entry.target.kind !== 'ACCOUNT') return false;
        const targetSide = this.targetSideResolver(entry.target.ref);
        return targetSide != null && targetSide !== this.sourceSide;
    }

    onSave(): void {
        this.submitAttempted = true;
        // An empty entries list is valid (means "delete all" for this source+period).
        // Only validate if there is at least one entry with data.
        if (this.entries.length > 0) {
            const valid = this.entries.every((e) => !this.isAmountInvalid(e) && !this.isTargetInvalid(e));
            if (!valid) return;
        }

        const payload: ReclassModalSavePayload = {
            sourceRef: { ...this.sourceRef },
            period: this.period,
            statement: this.statement,
            entries: this.entries
                // REV-21999: `!== 0` rather than `> 0` — a negative amount is a legitimate entry
                // and must not be silently dropped from the payload the way it was before.
                .filter((e) => e.target != null && e.amount != null && (e.amount as number) !== 0)
                .map((e) => ({
                    id: e.id,
                    target: e.target as ReclassTargetSelection,
                    amount: e.amount as number,
                    description: e.description.trim() ? e.description.trim() : undefined,
                })),
        };
        this.saveClicked.emit(payload);
    }

    onCancel(): void {
        this.cancelClicked.emit();
    }

    /** True when modal is currently editing PY (used for the subtitle). */
    get isPY(): boolean {
        return this.period === ReclassificationPeriod.PREVIOUS_YEAR;
    }

    trackByUiKey(_index: number, entry: EditableEntry): string {
        return entry.uiKey;
    }
}
